import React, { useEffect, useState } from 'react';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { notificationController } from '@app/controllers/notificationController';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { getRechargesAction, saveRechargesAction } from '@app/store/slices/userSlice';
import { useNavigate } from 'react-router-dom';
import Typography from 'antd/lib/typography/Typography';
import Title from 'antd/lib/typography/Title';
import { PageTitle } from '../common/PageTitle/PageTitle';

interface Recharge {
    amount: number;
    bonusPercentage: number;
    customLabel: string;
}

interface ValidationErrors {
    amount?: string;
    bonusPercentage?: string;
    customLabel?: string;
}

export const ManageRecharges: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [recharges, setRecharges] = useState<Recharge[]>([{ amount: 0, bonusPercentage: 0, customLabel: '' }]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors[]>([]);




    useEffect(() => {

        fetchData();



    }, []);


    const fetchData = async () => {
        try {
            const response = await dispatch(getRechargesAction(recharges)).unwrap();
            if (response.success) {
                const data = response?.data.length == 0 ? [{ amount: 0, bonusPercentage: 0, customLabel: '' }] : response?.data;
                setRecharges(data);
                // Initialize validation errors array
                setValidationErrors(new Array(data.length).fill({}));
            } else {
                notificationController.error({ message: response.message || 'Failed to get recharges' });
            }
        } catch (error) {
            notificationController.error({ message: error.message || 'Failed to get recharges' });
        }

    }

    const handleAddRow = () => {
        setRecharges([...recharges, { amount: 0, bonusPercentage: 0, customLabel: '' }]);
        setValidationErrors([...validationErrors, {}]);
    };

    const handleDeleteRow = (index: number) => {
        const updatedRecharges = recharges.filter((_, i) => i !== index);
        const updatedErrors = validationErrors.filter((_, i) => i !== index);
        setRecharges(updatedRecharges);
        setValidationErrors(updatedErrors);
    };

    const validateField = (field: keyof Recharge, value: number | string): string => {
        if (field === 'amount') {
            const numValue = Number(value);
            if (value === '' || value === null || value === undefined) return 'Recharge Amount is required';
            if (isNaN(numValue) || numValue < 0) return 'Recharge Amount must be a non-negative number';
        } else if (field === 'bonusPercentage') {
            const numValue = Number(value);
            if (value === '' || value === null || value === undefined) return 'Bonus Percentage is required';
            if (isNaN(numValue) || numValue < 0) return 'Bonus Percentage must be a non-negative number';
        } else if (field === 'customLabel') {
            if (!value || value.toString().trim() === '') return 'Custom Label is required';
        }
        return '';
    };

    const handleInputChange = (index: number, field: keyof Recharge, value: number | string) => {
        const updatedRecharges = [...recharges];
        updatedRecharges[index][field] = value as never;
        setRecharges(updatedRecharges);

        // Clear validation error for this field
        const updatedErrors = [...validationErrors];
        if (!updatedErrors[index]) updatedErrors[index] = {};
        updatedErrors[index][field] = '';
        setValidationErrors(updatedErrors);
    };

    const validateAllFields = (): boolean => {
        const errors: ValidationErrors[] = [];
        let hasErrors = false;

        recharges.forEach((recharge, index) => {
            const rechargeErrors: ValidationErrors = {};
            
            const amountError = validateField('amount', recharge.amount);
            if (amountError) {
                rechargeErrors.amount = amountError;
                hasErrors = true;
            }

            const bonusError = validateField('bonusPercentage', recharge.bonusPercentage);
            if (bonusError) {
                rechargeErrors.bonusPercentage = bonusError;
                hasErrors = true;
            }

            const customLabelError = validateField('customLabel', recharge.customLabel);
            if (customLabelError) {
                rechargeErrors.customLabel = customLabelError;
                hasErrors = true;
            }

            errors[index] = rechargeErrors;
        });

        setValidationErrors(errors);
        return !hasErrors;
    };

    const handleSave = async () => {
        if (!validateAllFields()) {
            notificationController.error({ message: 'Please fix all validation errors before saving' });
            return;
        }

        try {
            const response = await dispatch(saveRechargesAction({recharges})).unwrap();
            if (response.success) {
                notificationController.success({ message: t('common.success') });
                fetchData();
            } else {
                notificationController.error({ message: response.message || 'Failed to save recharges' });
            }
        } catch (error) {
            notificationController.error({ message: error.message || 'Failed to save recharges' });
        }
    };

    return (
        <div>
            <h3><strong>Manage Recharge Offers</strong></h3>

            <BaseRow gutter={[16, 16]}>
                {recharges.map((recharge, index) => (
                    <BaseRow key={index} gutter={[16, 16]} style={{ marginBottom: 16, display: 'contents' }}>
                        <BaseCol span={8}>
                            <label>Recharge Amount <span style={{ color: 'red' }}>*</span></label>
                            <BaseInput
                                type="number"
                                placeholder="Recharge Amount"
                                value={recharge.amount}
                                onChange={(e) => handleInputChange(index, 'amount', parseFloat(e.target.value) || 0)}
                                status={validationErrors[index]?.amount ? 'error' : ''}
                            />
                            {validationErrors[index]?.amount && (
                                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {validationErrors[index].amount}
                                </div>
                            )}
                        </BaseCol>
                        <BaseCol span={6}>
                            <label>Recharge Bonus(In Percentage) <span style={{ color: 'red' }}>*</span></label>
                            <BaseInput
                                type="number"
                                placeholder="Bonus Percentage"
                                value={recharge.bonusPercentage}
                                onChange={(e) => handleInputChange(index, 'bonusPercentage', parseFloat(e.target.value) || 0)}
                                status={validationErrors[index]?.bonusPercentage ? 'error' : ''}
                            />
                            {validationErrors[index]?.bonusPercentage && (
                                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {validationErrors[index].bonusPercentage}
                                </div>
                            )}
                        </BaseCol>
                        <BaseCol span={6}>
                            <label>Custom Label <span style={{ color: 'red' }}>*</span></label>
                            <BaseInput
                                type="text"
                                placeholder="Custom Label"
                                value={recharge.customLabel || ''}
                                onChange={(e) => handleInputChange(index, 'customLabel', e.target.value)}
                                status={validationErrors[index]?.customLabel ? 'error' : ''}
                            />
                            {validationErrors[index]?.customLabel && (
                                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {validationErrors[index].customLabel}
                                </div>
                            )}
                        </BaseCol>
                        <BaseCol span={4}>
                            <BaseButton
                                style={{ marginTop: '10px' }}
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteRow(index)}
                            />
                        </BaseCol>
                    </BaseRow>
                ))}
            </BaseRow>

            <BaseRow gutter={[16, 16]} style={{ marginTop: '10px' }}>
                <BaseCol>
                    <BaseButton type="dashed" icon={<PlusOutlined />} onClick={handleAddRow}>
                        Add Row
                    </BaseButton>
                </BaseCol>
            </BaseRow>

            <BaseRow gutter={[16, 16]} style={{ marginTop: '10px' }}>
                <BaseCol>
                    <BaseButton type="primary" onClick={handleSave}>
                        Save
                    </BaseButton>
                </BaseCol>
            </BaseRow>
        </div>
    );
};